import React, { memo } from 'react';
import { SUPPORTED_COUNTRIES } from '@honeybook/i18n';
import type { SupportedCountry } from '@honeybook/i18n';
import HBInputField, { HBInputFieldProps } from '../HBInputField/HBInputField';
import { getAddressFieldConfig } from './HBAddress.utils';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';

export type HBAddressPostalFieldProps = Omit<
    HBInputFieldProps,
    'label' | 'ref'
> & {
    /** The currently selected country code. Determines label and HTML5 pattern validation. */
    country: string;
    /** Override the auto-derived label (e.g. ZIP Code, Postal Code, Postcode). */
    label?: string;
    /** Identifier segment appended to the component name to build data-testid. */
    testIdName?: string;
    /** Explicit test ID string; sets data-testid directly. */
    testId?: string;
};

const HBAddressPostalField = ({
    country,
    label,
    placeholder,
    ...props
}: HBAddressPostalFieldProps) => {
    const t = useHBTranslation();
    const isSupportedCountry = (SUPPORTED_COUNTRIES as string[]).includes(
        country
    );
    const config = isSupportedCountry
        ? getAddressFieldConfig(country as SupportedCountry)
        : null;

    const derivedLabel =
        label ??
        t(config ? config.postalCodeKey : 'hbAddress.postalCode.postalCode');

    return (
        <HBInputField
            {...props}
            label={derivedLabel}
            placeholder={placeholder ?? config?.postalCodePlaceholder}
            {...(config && { pattern: config.postalCodePattern.source })}
        />
    );
};

export default memo(HBAddressPostalField);
